<template>
  <!-- Dashboard Agent -->
  <div v-if="checkRole('Agent')" class="row align-items-stretch mb-25">
    <!-- Bloc de bienvenue avec image -->
    <div class="col-lg-4 col-md-12 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 welcome-box h-100">
        <div class="card-body p-15 p-sm-20">
          <div class="row align-items-center h-100">
            <div class="col-7">
              <h3 class="fw-semibold mb-8">
                Bonjour, <span class="fw-bold">{{ userName ? userName : "" }}!</span>
              </h3>
              <span class="d-block text-black-emphasis fs-md-15">
                Votre espace de validation. Chaque contrôle effectué garantit la conformité des dossiers.
              </span>
            </div>
            <div class="col-5 text-center">
              <img
                src="../../../assets/images/welcome/besc.png"
                alt="welcome-image"
                class="img-fluid"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-folder text-primary" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.totalFiles }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Files assignées</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-check text-success" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.completedTasks }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Dossiers traités</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-time text-warning" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.pendingTasks }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">En Validation</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 mt-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Mes Files de Validation</h4>
          <button @click="loadAgentData" class="btn btn-outline-primary btn-sm">
            <i class="flaticon-refresh me-1"></i> Actualiser
          </button>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>

          <div v-else-if="assignedQueues.length === 0" class="text-center py-4">
            <p class="text-muted">Aucune file assignée pour le moment</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Nom de la file</th>
                  <th>Code</th>
                  <th>Niveau de risque</th>
                  <th>Nombre de dossiers</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="queue in assignedQueues" :key="queue.id">
                  <td class="fw-medium">{{ queue.name }}</td>
                  <td>{{ queue.code }}</td>
                  <td>
                    <span class="badge" :class="getRiskBadgeClass(queue.maxRiskLevel)">
                      {{ getRiskLabel(queue.maxRiskLevel) }}
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-primary">
                      {{ getBescsCount(queue.id) }} dossiers
                    </span>
                  </td>
                  <td class="text-end">
                    <div class="dropdown">
                      <span 
                        class="badge text-white bg-primary fs-15 dropdown-toggle" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style="cursor: pointer;"
                      >
                        Actions
                        <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                      </span>
                      <ul class="dropdown-menu">
                        <li>
                          <router-link
                            :to="{ name: 'AgentFilesPage', query: { queueId: queue.id } }"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-folder-open lh-1 me-8 position-relative top-1"></i>
                            Voir les dossiers
                          </router-link>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Tâches récentes -->
    <div class="col-12 mt-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Tâches Récentes</h4>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>

          <div v-else-if="recentTasks.length === 0" class="text-center py-4">
            <p class="text-muted">Aucune tâche récente</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Référence BESC</th>
                  <th>File</th>
                  <th>Statut</th>
                  <th>Priorité</th>
                  <th>Date limite</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="task in recentTasks" :key="task.id">
                  <td class="fw-medium">{{ task.validationTask.besc?.refbesc }}</td>
                  <td>{{ task.validationTask.validationqueue?.name }}</td>
                  <td>
                    <span v-html="getStatusBadgeAgent(task.result)"></span>
                  </td>
                  <td>
                    <span v-html="getPriorityBadge(task.validationTask.priority)"></span>
                  </td>
                  <td>{{ format_date(task.validationTask.sla_due_at) }}</td>
                  <td class="text-end">
                    <div class="dropdown">
                      <span 
                        class="badge text-white bg-primary fs-15 dropdown-toggle" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style="cursor: pointer;"
                      >
                        Actions
                        <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                      </span>
                      <ul class="dropdown-menu">
                        <li v-if="task.validationTask.besc?.id">
                          <router-link
                            :to="{ name: 'ViewBesc', params: { id: task.validationTask.besc.id } }"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-eye lh-1 me-8 position-relative top-1"></i>
                            Détails
                          </router-link>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Dashboard Superviseur -->
  <div v-else-if="checkRole('Superviseur')" class="row align-items-stretch mb-25">
    <!-- Bloc de bienvenue avec image -->
    <div class="col-lg-4 col-md-12 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 welcome-box h-100">
        <div class="card-body p-15 p-sm-20">
          <div class="row align-items-center h-100">
            <div class="col-7">
              <h3 class="fw-semibold mb-8">
                Bonjour, <span class="fw-bold">{{ userName ? userName : "" }}!</span>
              </h3>
              <span class="d-block text-black-emphasis fs-md-15">
                Votre espace de supervision. Contrôlez et validez les dossiers traités par les agents.
              </span>
            </div>
            <div class="col-5 text-center">
              <img
                src="../../../assets/images/welcome/besc.png"
                alt="welcome-image"
                class="img-fluid"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-time text-warning" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.enAttente }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">En validation</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-check text-success" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.approuves }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Approuvés</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-close text-danger" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.rejetes }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Rejetés</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 mt-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">
            {{ viewModeSuperviseur === 'PENDING' ? 'Validations en attente' : 'Validations traitées' }}
          </h4>
          <div class="d-flex gap-2">
            <div class="btn-group me-2">
              <button
                class="btn btn-sm"
                :class="viewModeSuperviseur === 'PENDING' ? 'btn-primary' : 'btn-outline-primary'"
                @click="() => { viewModeSuperviseur = 'PENDING'; loadSuperviseurData(); }"
              >
                En validation
              </button>
              <button
                class="btn btn-sm"
                :class="viewModeSuperviseur === 'PROCESSED' ? 'btn-primary' : 'btn-outline-primary'"
                @click="() => { viewModeSuperviseur = 'PROCESSED'; loadSuperviseurData(); }"
              >
                Traités
              </button>
            </div>
            <button @click="loadSuperviseurData" class="btn btn-outline-primary btn-sm">
              <i class="flaticon-refresh me-1"></i> Actualiser
            </button>
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>

          <div v-else-if="validations.length === 0" class="text-center py-4">
            <p class="text-muted">Aucune validation en attente</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Référence BESC</th>
                  <th>Agent</th>
                  <th>File</th>
                  <th>{{ viewModeSuperviseur === 'PENDING' ? 'Date terminé' : 'Date validation' }}</th>
                  <th>Statut</th>
                  <th class="text-end" v-if="viewModeSuperviseur === 'PENDING'">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="validation in validations" :key="validation.id">
                  <td class="fw-medium">{{ validation.refbesc }}</td>
                  <td>
                    <div v-if="validation.assignedAgent">
                      {{ validation.assignedAgent.nom }} {{ validation.assignedAgent.prenom }}
                      <div class="text-muted small">{{ validation.assignedAgent.email }}</div>
                    </div>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <div v-if="validation.queue">
                      {{ validation.queue.name }}
                      <div class="text-muted small">{{ validation.queue.code }}</div>
                    </div>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>{{ viewModeSuperviseur === 'PENDING' ? format_date(validation.completedAt) : format_date(validation.supervisorValidatedAt) }}</td>
                  <td>
                    <span class="badge" :class="getStatusClassSuperviseur(validation.supervisorDecision)">
                      {{ getStatusLabelSuperviseur(validation.supervisorDecision) }}
                    </span>
                  </td>
                  <td class="text-end" v-if="viewModeSuperviseur === 'PENDING'">
                    <div class="dropdown">
                      <span 
                        class="badge text-white bg-primary fs-15 dropdown-toggle" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style="cursor: pointer;"
                      >
                        Actions
                        <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                      </span>
                      <ul class="dropdown-menu">
                        <li>
                          <a
                            href="javascript:void(0);"
                            @click="approuverValidation(validation.id)"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-check lh-1 me-8 position-relative top-1"></i>
                            Approuver
                          </a>
                        </li>
                        <li>
                          <a
                            href="javascript:void(0);"
                            @click="rejeterValidation(validation.id)"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-close lh-1 me-8 position-relative top-1"></i>
                            Rejeter
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
            <PaginationComponent
              :page="pageSuperviseur"
              :totalPages="totalPagesSuperviseur"
              :totalElements="totalElementsSuperviseur"
              :limit="limitSuperviseur"
              @paginate="handlePaginateSuperviseur"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Dashboard Transitaire -->
  <div v-else-if="checkRole('Transitaire')" class="row align-items-stretch mb-25">
    <!-- Bloc de bienvenue avec image -->
    <div class="col-lg-4 col-md-12 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 welcome-box h-100">
        <div class="card-body p-15 p-sm-20">
          <div class="row align-items-center h-100">
            <div class="col-7">
              <h3 class="fw-semibold mb-8">
                Bonjour, <span class="fw-bold">{{ userName ? userName : "" }}!</span>
              </h3>
              <span class="d-block text-black-emphasis fs-md-15">
                Votre espace de gestion BESC. Chaque dossier soumis est un pas vers une exportation réussie..
              </span>
            </div>
            <div class="col-5 text-center">
              <img
                src="../../../assets/images/welcome/besc.png"
                alt="welcome-image"
                class="img-fluid"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-folder text-primary" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.totalDossiers }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Dossiers totaux</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-check text-success" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.dossiersValides }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Dossiers validés</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg col-sm-4 mb-25 mb-lg-0">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="d-flex align-items-center">
            <div class="flex-shrink-0">
              <i class="flaticon-time text-warning" style="font-size: 36px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 28px;">
                {{ stats.dossiersEnValidation }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">En cours de validation</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 mt-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Dossiers en Validation / Validés</h4>
          <!-- <router-link to="/transitaire-dossiers" class="btn btn-outline-primary btn-sm">
            Voir tous les dossiers
          </router-link> -->
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>
          <div v-else-if="dossiers.length === 0" class="text-center py-4">
            <p class="text-muted">Aucun dossier en validation ou validé</p>
          </div>
          <div v-else class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Référence BESC</th>
                  <th>Destination</th>
                  <th>Statut</th>
                  <th>Date création</th>
                  <th>Date validation</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dossier in dossiers" :key="dossier.id">
                  <td class="fw-medium">{{ dossier.refbesc }}</td>
                  <td>{{ dossier.destination || '—' }}</td>
                  <td>
                    <span v-html="getStatusBadge(dossier.status)"></span>
                  </td>
                  <td>{{ format_date(dossier.created_at) }}</td>
                  <td>{{ format_date(dossier.validated_at) }}</td>
                  <td class="text-end">
                    <div class="dropdown">
                      <span class="badge text-white bg-primary fs-15 dropdown-toggle" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style="cursor: pointer;">
                        Actions
                        <i class="flaticon-chevron-2 position-relative ms-5 top-2 fs-15"></i>
                      </span>
                      <ul class="dropdown-menu">
                        <li>
                          <router-link
                            v-if="dossier.id"
                            :to="{ name: 'ViewBesc', params: { id: dossier.id } }"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-eye lh-1 me-8 position-relative top-1"></i>
                            Détails
                          </router-link>
                        </li>
                        <li v-if="dossier.status === 'VALIDE'">
                          <a 
                            href="javascript:void(0);"
                            @click="telechargerPDF(dossier.id)"
                            class="dropdown-item d-flex align-items-center"
                          >
                            <i class="flaticon-file-1 lh-1 me-8 position-relative top-1"></i>
                            Télécharger PDF
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import JwtService from "@/services/JwtService";
import { error, format_date, success } from "@/utils/utils";
import ApiService from "@/services/ApiService";
import { BescInQueue } from '@/models/BescInQueue';
import { ValidationQueues } from '@/models/ValidationTask';
import PaginationComponent from '@/components/Utilities/Pagination.vue';

interface AgentStats {
  totalFiles: number;
  pendingTasks: number;
  completedTasks: number;
  inProgressTasks: number;
}

interface TransitaireStats {
  totalDossiers: number;
  dossiersValides: number;
  dossiersEnValidation: number;
}

interface SuperviseurStats {
  enAttente: number;
  approuves: number;
  rejetes: number;
}

interface ValidationItem {
  id: string;
  refbesc: string;
  completedAt: string | null;
  supervisorValidatedAt: string | null;
  waitingTime: string | null;
  supervisorDecision: 'APPROVED' | 'REJECTED' | null;
  assignedAgent?: {
    id: string;
    nom: string;
    prenom: string;
    email?: string | null;
  };
  queue?: {
    id: string;
    name: string;
    code: string;
  };
}

interface ValidationQueue {
  id: string;
  name: string;
  code: string;
  description: string;
  minRiskLevel: string;
  maxRiskLevel: string;
  pays: any;
  port: any;
}

interface ValidationTaskInfo {
  id: string;
  priority: string;
  validationqueue: {
    id: string;
    name: string;
  };
  besc: {
    id: string;
    refbesc: string;
  };
  sla_due_at: string;
}

interface RecentTask {
  id: string;
  result: string; 
  validationTask: ValidationTaskInfo;
  tache: {
    id: string;
    title: string;
    check_code: string;
  };
}

interface Dossier {
  id: string;
  refbesc: string;
  client: string;
  destination: string;
  status: string;
  created_at: string;
  validated_at?: string;
}

export default defineComponent({
  name: "WhatHappening",
  components: { PaginationComponent },
  setup() {
    const userName = ref("");
    const roles = ref<Array<string>>(JwtService.getRole());
    const loading = ref(false);
    
    const stats = ref<AgentStats & TransitaireStats & SuperviseurStats>({
      totalFiles: 0,
      pendingTasks: 0,
      completedTasks: 0,
      inProgressTasks: 0,
      totalDossiers: 0,
      dossiersValides: 0,
      dossiersEnValidation: 0,
      enAttente: 0,
      approuves: 0,
      rejetes: 0
    });

    const assignedQueues = ref<ValidationQueue[]>([]);
    const recentTasks = ref<RecentTask[]>([]);
    const queues = ref<ValidationQueues[]>([]);
    const bescs = ref<BescInQueue[]>([]);
    const dossiers = ref<Dossier[]>([]);
    const validations = ref<ValidationItem[]>([]);
    const viewModeSuperviseur = ref<'PENDING' | 'PROCESSED'>('PENDING');
    
    const pageSuperviseur = ref(1);
    const limitSuperviseur = ref(10);
    const totalPagesSuperviseur = ref(0);
    const totalElementsSuperviseur = ref(0);

    const checkRole = (name: string) => {
      return roles.value.includes(name);
    };

    async function loadQueues() {
      try {
        const { data } = await ApiService.get("/validation-queues");
        queues.value = data.data || [];
        await loadBescsInQueues();
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur de chargement des files");
      }
    }

    const loadAgentData = async () => {
      loading.value = true;
      try {
        const user = JwtService.getUser();
        if (!user) {
          error("Utilisateur non connecté");
          return;
        }

        const statsResponse = await ApiService.get(`/agent/stats/${user.userId}`);
        Object.assign(stats.value, statsResponse.data.data);

        const queuesResponse = await ApiService.get(`/agent/queues/${user.userId}`);
        assignedQueues.value = queuesResponse.data.data;

        const tasksResponse = await ApiService.get(`/agent/recent-tasks/${user.userId}?limit=5`);
        recentTasks.value = tasksResponse.data.data.map((check: any) => ({
          ...check
        }));

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des données');
      } finally {
        loading.value = false;
      }
    };

    const loadTransitaireData = async () => {
      loading.value = true;
      try {
        const user = JwtService.getUser();
        if (!user) {
          error("Utilisateur non connecté");
          return;
        }

        const statsResponse = await ApiService.get(`/transitaire/stats/${user.userId}`);
        Object.assign(stats.value, statsResponse.data.data);

        const dossiersResponse = await ApiService.get(
          `/transitaire/dossiers/${user.userId}?statuts=EN_VALIDATION,VALIDE&limit=10`
        );
        console.log("nouveauuuuuuuuuu",dossiersResponse);
        dossiers.value = dossiersResponse.data.data.data;

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des données');
      } finally {
        loading.value = false;
      }
    };

    const loadSuperviseurData = async (p = pageSuperviseur.value, l = limitSuperviseur.value) => {
      loading.value = true;
      try {
        pageSuperviseur.value = p;
        limitSuperviseur.value = l;

        const endpoint = viewModeSuperviseur.value === 'PENDING' 
          ? '/supervisor/validations' 
          : '/supervisor/approuve';

        const res = await ApiService.get(`${endpoint}?page=${p}&limit=${l}`);
        validations.value = res.data.data.data || [];
        totalPagesSuperviseur.value = res.data.data.totalPages || 0;
        totalElementsSuperviseur.value = res.data.data.totalElements || 0;

        // Calculer les stats depuis toutes les validations (en attente + traitées)
        const pendingRes = await ApiService.get('/supervisor/validations?page=1&limit=0');
        const processedRes = await ApiService.get('/supervisor/approuve?page=1&limit=0');
        
        const allPending = pendingRes.data.data.data || [];
        const allProcessed = processedRes.data.data.data || [];

        stats.value.enAttente = allPending.length;
        stats.value.approuves = allProcessed.filter(v => v.supervisorDecision === 'APPROVED').length;
        stats.value.rejetes = allProcessed.filter(v => v.supervisorDecision === 'REJECTED').length;

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des données');
      } finally {
        loading.value = false;
      }
    };

    const handlePaginateSuperviseur = ({ page_, limit_ }: any) => {
      loadSuperviseurData(page_, limit_);
    };

    const approuverValidation = async (validationId: string) => {
      try {
        await ApiService.post(`/supervisor/validate-dossier/${validationId}`, {
          decision: 'APPROVED',
          supervisorComment: 'Approuvé par le superviseur'
        });
        success('Dossier approuvé avec succès');
        await loadSuperviseurData();
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de l\'approbation');
      }
    };

    const rejeterValidation = async (validationId: string) => {
      try {
        await ApiService.post(`/supervisor/validate-dossier/${validationId}`, {
          decision: 'REJECTED',
          supervisorComment: 'Rejeté par le superviseur'
        });
        success('Dossier rejeté avec succès');
        await loadSuperviseurData();
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du rejet');
      }
    };

    const getStatusClassSuperviseur = (decision: 'APPROVED' | 'REJECTED' | null): string => {
  if (decision === 'APPROVED') return 'bg-success';
  if (decision === 'REJECTED') return 'bg-danger';
  return 'bg-warning text-dark'; // En attente de décision superviseur
};

const getStatusLabelSuperviseur = (decision: 'APPROVED' | 'REJECTED' | null): string => {
  if (decision === 'APPROVED') return 'Approuvé';
  if (decision === 'REJECTED') return 'Rejeté';
  return 'En validation'; // cohérent avec SUBMITTED
};

    const getRiskBadgeClass = (riskLevel: string): string => {
      const classes: Record<string, string> = {
        'HIGH': 'bg-danger',
        'MEDIUM': 'bg-warning',
        'LOW': 'bg-success'
      };
      return classes[riskLevel] || 'bg-secondary';
    };

    const getRiskLabel = (riskLevel: string): string => {
      const labels: Record<string, string> = {
        'HIGH': 'Élevé',
        'MEDIUM': 'Moyen',
        'LOW': 'Faible'
      };
      return labels[riskLevel] || riskLevel;
    };

    async function loadBescsInQueues() {
      try {
        const { data } = await ApiService.get("/bescs/in-queues");
        bescs.value = data.data || [];
      } catch (err: any) {
        console.error("Erreur chargement BESCs:", err);
      }
    }

    const getBescsByQueue = (queueId: string): BescInQueue[] => {
      return bescs.value.filter(b => b.queueId === queueId);
    };

    const getBescsCount = (queueId: string): number => {
      return getBescsByQueue(queueId).length;
    };

   const getStatusBadgeAgent = (status: string): string => {
  const badges: Record<string, string> = {
    // Statuts des tâches agent
    'PENDING':                 '<span class="badge bg-warning text-dark">En validation</span>',
    'IN_PROGRESS':             '<span class="badge bg-info">En cours</span>',
    'COMPLETED':               '<span class="badge bg-success">Validé</span>',
    'RETURNED_FOR_CORRECTION': '<span class="badge bg-warning text-dark">Retour correction</span>',
    'CANCELLED':               '<span class="badge bg-dark">Annulée</span>',
  };
  return badges[status] || `<span class="badge bg-secondary">${status}</span>`;
};

    const getPriorityBadge = (priority: string): string => {
      const badges: Record<string, string> = {
        'LOW': '<span class="badge bg-success">Faible</span>',
        'NORMAL': '<span class="badge bg-info">Normale</span>',
        'HIGH': '<span class="badge bg-warning">Élevée</span>',
        'CRITICAL': '<span class="badge bg-danger">Critique</span>'
      };
      return badges[priority] || `<span class="badge bg-secondary">${priority}</span>`;
    };
const getStatusBadge = (status: string): string => {
  const badges: Record<string, string> = {
    // Vrais statuts BDD
    'DRAFT':                   '<span class="badge bg-secondary">En attente</span>',
    'SUBMITTED':               '<span class="badge bg-warning text-dark">En validation</span>',
    'CORRECTIONS_IN_PROGRESS': '<span class="badge bg-info">Correction en cours</span>',
    'CORRECTIONS_COMPLETED':   '<span class="badge bg-primary">Corrections terminées</span>',
    'APPROVED':                '<span class="badge bg-success">Approuvé</span>',
    'REJECTED':                '<span class="badge bg-danger">Rejeté</span>',
    'ISSUED':                  '<span class="badge bg-primary">Émis</span>',
    'CANCELLED':               '<span class="badge bg-dark">Annulé</span>',
    // Anciens statuts renvoyés par le backend transitaire
    'VALIDE':                  '<span class="badge bg-success">Approuvé</span>',
    'EN_VALIDATION':           '<span class="badge bg-warning text-dark">En validation</span>',
    'REJETE':                  '<span class="badge bg-danger">Rejeté</span>',
    'BROUILLON':               '<span class="badge bg-secondary">En attente</span>',
  };
  return badges[status] || `<span class="badge bg-secondary">${status}</span>`;
};

    const telechargerPDF = async (id: string) => {
      try {
        const response = await ApiService.getFile(`/besc/${id}/download-official`);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `DossierOfficiel_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          link.remove();
          window.URL.revokeObjectURL(url);
        }, 100);
        success('Document officiel téléchargé avec succès !');
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du téléchargement du PDF');
      }
    };

    onMounted(() => {
      if(JwtService.getUser()){
        userName.value = JwtService.getUserName()!;
      }

      if (checkRole('Agent')) {
        loadAgentData();
        loadQueues();
      } else if (checkRole('Transitaire')) {
        loadTransitaireData();
      } else if (checkRole('Superviseur')) {
        loadSuperviseurData();
      }
    });

    return {
      userName,
      checkRole,
      loading,
      stats,
      assignedQueues,
      recentTasks,
      dossiers,
      validations,
      viewModeSuperviseur,
      pageSuperviseur,
      limitSuperviseur,
      totalPagesSuperviseur,
      totalElementsSuperviseur,
      loadAgentData,
      loadSuperviseurData,
      handlePaginateSuperviseur,
      approuverValidation,
      rejeterValidation,
      getRiskBadgeClass,
      getRiskLabel,
      getStatusBadgeAgent,
      getPriorityBadge,
      getStatusBadge,
      getStatusClassSuperviseur,
      getStatusLabelSuperviseur,
      format_date,
      getBescsByQueue,
      getBescsCount,
      telechargerPDF
    };
  }
});
</script>